import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  CaretLeft, CaretRight, Plus, Users, ListChecks, Bell,
  CalendarBlank, MapPin, Clock, User as UserIcon, Briefcase, CurrencyCircleDollar,
  Phone, Car, Cake, Note, CheckSquare, Square, Trash, Lock, UsersThree, Buildings,
  Printer, FilePdf, Warning, Palette,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DatePickerDMY from '../components/DatePickerDMY';
import CategoryManager, { CategoryIcon } from '../components/calendar/CategoryManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 195 — legacy calendar aggregator (meetings + building tasks).
// Phase 800 — Extended to combine personal events created via
// `/api/calendar/personal-events`. The personal endpoint is queried in
// parallel and merged client-side; the legacy endpoint is untouched to
// preserve backward compatibility with the Building Management flows.

const monthNamesEl = ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'];
const monthNamesEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const weekdaysEl = ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'];
const weekdaysEn = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const fmtDmy = (s) => {
  const d = parseYmd(s);
  return d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : s;
};

const daysBetween = (a, b) => {
  const da = parseYmd(a);
  const db_ = parseYmd(b);
  if (!da || !db_) return null;
  return Math.round((db_.getTime() - da.getTime()) / 86400000);
};

// -------------------------------------------------------------------
// Phase 800 — Personal event type registry.
// Each row defines its own colour, filter chip, dialog icon and label
// (in both languages). Extend here to add a new type.
// -------------------------------------------------------------------
const EVENT_TYPES = [
  { key: 'building',    label_en: 'Building',    label_el: 'Πολυκατοικία', Icon: Buildings,              bg: 'bg-blue-600',    text: 'text-white',    dot: 'bg-blue-600' },
  { key: 'personal',    label_en: 'Personal',    label_el: 'Προσωπικό',   Icon: UserIcon,               bg: 'bg-purple-600',  text: 'text-white',    dot: 'bg-purple-600' },
  { key: 'task',        label_en: 'Task',        label_el: 'Εργασία',     Icon: ListChecks,             bg: 'bg-emerald-600', text: 'text-white',    dot: 'bg-emerald-600' },
  { key: 'appointment', label_en: 'Appointment', label_el: 'Ραντεβού',    Icon: Briefcase,              bg: 'bg-orange-500',  text: 'text-white',    dot: 'bg-orange-500' },
  { key: 'finance',     label_en: 'Finance',     label_el: 'Οικονομικά',  Icon: CurrencyCircleDollar,   bg: 'bg-red-600',     text: 'text-white',    dot: 'bg-red-600' },
  { key: 'call',        label_en: 'Call',        label_el: 'Κλήση',       Icon: Phone,                  bg: 'bg-cyan-600',    text: 'text-white',    dot: 'bg-cyan-600' },
  { key: 'vehicle',     label_en: 'Vehicle',     label_el: 'Όχημα',       Icon: Car,                    bg: 'bg-zinc-500',    text: 'text-white',    dot: 'bg-zinc-500' },
  { key: 'birthday',    label_en: 'Birthday',    label_el: 'Γενέθλια',    Icon: Cake,                   bg: 'bg-pink-500',    text: 'text-white',    dot: 'bg-pink-500' },
  { key: 'note',        label_en: 'Note',        label_el: 'Σημείωση',    Icon: Note,                   bg: 'bg-yellow-400',  text: 'text-zinc-900', dot: 'bg-yellow-400' },
];
const typeByKey = (k) => EVENT_TYPES.find(x => x.key === k);
// Legacy meeting = building-blue; legacy task from db.tasks = green.
const legacyMeetingColor  = { bg: 'bg-zinc-900',   text: 'text-white', dot: 'bg-zinc-900' };
const legacyNextMeetingColor = { bg: 'bg-amber-500', text: 'text-white', dot: 'bg-amber-500' };
const legacyTaskColor     = { bg: 'bg-emerald-600', text: 'text-white', dot: 'bg-emerald-600' };

const PRIORITY_META = {
  high:   { dot: 'bg-red-500',    label_en: 'High',   label_el: 'Υψηλή' },
  medium: { dot: 'bg-orange-400', label_en: 'Medium', label_el: 'Μεσαία' },
  low:    { dot: 'bg-emerald-500',label_en: 'Low',    label_el: 'Χαμηλή' },
};

const VISIBILITY_META = [
  { key: 'private',   Icon: Lock,       label_en: 'Private (only me)',   label_el: 'Ιδιωτικό (μόνο εγώ)' },
  { key: 'team',      Icon: UsersThree, label_en: 'Share with Team',     label_el: 'Κοινή χρήση με ομάδα' },
  { key: 'committee', Icon: Buildings,  label_en: 'Building Committee',  label_el: 'Επιτροπή Πολυκατοικίας' },
];

export default function CalendarPage() {
  const { t, language } = useLanguage();
  useAuth();
  const navigate = useNavigate();

  const [cursor, setCursor] = useState(() => {
    const t0 = new Date();
    return new Date(t0.getFullYear(), t0.getMonth(), 1);
  });
  const [events, setEvents] = useState({ meetings: [], next_meetings: [], tasks: [] });
  const [personalEvents, setPersonalEvents] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [taskDialog, setTaskDialog] = useState(null);   // legacy building tasks
  const [eventDialog, setEventDialog] = useState(null); // event-clicked in grid
  const [personalDialog, setPersonalDialog] = useState(null); // Phase 800
  // Phase 812 — Day-events overflow modal ("+X more" opener).
  //   { key: 'YYYY-MM-DD', date: Date, events: [...] }
  const [dayEventsDialog, setDayEventsDialog] = useState(null);
  // Phase 810 — Confirmation dialog for deletions (styled, not window.confirm).
  //   { kind: 'personal'|'task', id, title }
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Phase 810 — Print / Export dialog.
  const [printDialog, setPrintDialog] = useState(null);
  const [printPreview, setPrintPreview] = useState(null);
  const [printBusy, setPrintBusy] = useState(false);

  // Phase 943 — Admin-editable Calendar categories. Fetched from
  // /api/calendar/event-kinds and used to build filter chips + legend.
  // Falls back to the hard-coded EVENT_TYPES if the API is unavailable
  // so the calendar never renders empty.
  const [eventKinds, setEventKinds] = useState([]);
  const [categoryMgrOpen, setCategoryMgrOpen] = useState(false);
  // Phase 943 — Status filter: 'active' (default hides completed +
  // cancelled), 'completed', 'cancelled', 'all'.
  const [statusFilter, setStatusFilter] = useState('active');

  // Phase 800 — Multi-select filter chips. Empty set == "show all".
  const [activeTypeFilters, setActiveTypeFilters] = useState(() => new Set());
  const toggleTypeFilter = (k) => setActiveTypeFilters(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const clearTypeFilters = () => setActiveTypeFilters(new Set());

  const monthNames = language === 'el' ? monthNamesEl : monthNamesEn;
  const weekdays = language === 'el' ? weekdaysEl : weekdaysEn;

  const range = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const firstDow = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - firstDow);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridStart.getDate() + 41);
    return { start: ymd(gridStart), end: ymd(gridEnd), gridStart };
  }, [cursor]);

  const fetchEvents = useCallback(async () => {
    // Phase 1017 — Calendar is now a STANDALONE module. It no longer
    // pulls building meetings / next_meetings / building tasks from
    // /api/calendar/events. Only user-created personal events are
    // fetched. The empty `{meetings, next_meetings, tasks}` shape is
    // preserved as a stub so downstream code that iterates over these
    // arrays yields nothing and stays intact for the eventual rebuild.
    try {
      const personal = await axios.get(`${API}/calendar/personal-events?start=${range.start}&end=${range.end}`);
      setEvents({ meetings: [], next_meetings: [], tasks: [] });
      setPersonalEvents((personal.data && personal.data.events) || []);
    } catch {
      toast.error('Calendar load failed');
    }
  }, [range.start, range.end]);

  useEffect(() => {
    axios.get(`${API}/buildings`).then(r => setBuildings(r.data || [])).catch(() => {});
    // Phase 943 — Load admin-editable calendar kinds.
    axios.get(`${API}/calendar/event-kinds`).then(r => setEventKinds(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Phase 943 — Refresh kinds after admin edits categories.
  const refreshEventKinds = useCallback(() => {
    axios.get(`${API}/calendar/event-kinds`).then(r => setEventKinds(r.data || [])).catch(() => {});
  }, []);

  // Phase 943 — Merge fetched kinds with legacy fallback. Legacy is used
  // ONLY when the API returns nothing (fresh install, request failed).
  const kindsForUi = useMemo(() => {
    // Phase 1018 — Calendar is a manual-only module. Hide only the
    // Building-Management-derived kinds from the Calendar's filter
    // chips, legend, and event-kind dropdown:
    //   - 'building'      (Buildings filter — moved to Task Management)
    //   - 'next_meeting'  (auto-generated from a building's next meeting)
    // The other categories (Meeting, Task, Personal, Appointment,
    // Finance, Call, Vehicle, Birthday, Note) remain as valid manual
    // event categories the user selects when creating an Add Event
    // entry. A Calendar "Task" is a personal event tag — NOT the same
    // as a building task, which lives in the Task Management module.
    const HIDDEN = new Set(['building', 'next_meeting']);
    if (eventKinds && eventKinds.length) {
      return eventKinds
        .filter(k => k.active !== false)
        .filter(k => !HIDDEN.has(k.kind_key || k.event_kind_id))
        .map(k => ({
          key: k.kind_key || k.event_kind_id,
          name: k.name,
          color: k.color || '#64748b',
          icon: k.icon || 'Circle',
        }));
    }
    // Fallback to hardcoded list. This keeps the calendar usable if the
    // categories endpoint fails for any reason.
    return EVENT_TYPES
      .filter(t => !HIDDEN.has(t.key))
      .map(t => ({
        key: t.key,
        name: language === 'el' ? t.label_el : t.label_en,
        color: null,       // signals "use legacy tailwind class"
        _legacy: t,
        icon: null,
      }));
  }, [eventKinds, language]);
  const kindByKey = useCallback((k) => kindsForUi.find(x => x.key === k), [kindsForUi]);

  // Phase 943 — Human label for a kind. Uses admin-configured name
  // when available; falls back to a Greek/English translation of the
  // legacy hardcoded label.
  const kindLabel = useCallback((k) => {
    const found = kindByKey(k);
    if (found) return found.name;
    const legacy = EVENT_TYPES.find(x => x.key === k);
    return legacy ? (language === 'el' ? legacy.label_el : legacy.label_en) : k;
  }, [kindByKey, language]);

  // -----------------------------------------------------------------
  // Type filter helpers
  // -----------------------------------------------------------------
  const passesTypeFilter = useCallback((kind, event_type) => {
    if (activeTypeFilters.size === 0) return true;
    // Legacy events: check both the specific kind and the more generic
    // "building" kind (they've historically shared the blue chip).
    if (kind === 'meeting') return activeTypeFilters.has('meeting') || activeTypeFilters.has('building');
    if (kind === 'next_meeting') return activeTypeFilters.has('next_meeting') || activeTypeFilters.has('building');
    if (kind === 'task') return activeTypeFilters.has('task') || activeTypeFilters.has('building');
    if (kind === 'personal') return activeTypeFilters.has(event_type);
    return true;
  }, [activeTypeFilters]);

  // Phase 943 — Status filter. Hides completed/cancelled events by
  // default (`statusFilter === 'active'`). Completed tasks remain
  // reachable via the "Completed" chip. Legacy meetings never carry a
  // completion status so they always pass the "active" filter.
  const passesStatusFilter = useCallback((e) => {
    if (statusFilter === 'all') return true;
    if (e.kind === 'personal') {
      if (statusFilter === 'completed') return !!e.completed;
      // Personal events don't have a "cancelled" flag → treat as absent.
      if (statusFilter === 'cancelled') return false;
      return !e.completed;
    }
    if (e.kind === 'task') {
      const st = (e.status || 'pending').toLowerCase();
      if (statusFilter === 'completed') return st === 'completed';
      if (statusFilter === 'cancelled') return st === 'cancelled';
      return st !== 'completed' && st !== 'cancelled';
    }
    // Meetings / next_meetings — always visible unless the user
    // explicitly asked for completed/cancelled (which don't apply).
    if (statusFilter === 'completed' || statusFilter === 'cancelled') return false;
    return true;
  }, [statusFilter]);

  // Group events by date (yyyy-mm-dd)
  const grouped = useMemo(() => {
    const map = {};
    const push = (date, e) => {
      if (!date) return;
      if (!passesStatusFilter(e)) return;
      (map[date] ||= []).push(e);
    };
    (events.meetings || []).forEach(e => passesTypeFilter('meeting') && push(e.date, { ...e, kind: 'meeting' }));
    (events.next_meetings || []).forEach(e => passesTypeFilter('next_meeting') && push(e.date, { ...e, kind: 'next_meeting' }));
    (events.tasks || []).forEach(e => passesTypeFilter('task') && push(e.date, { ...e, kind: 'task' }));
    // Personal events surface under kind="personal".
    personalEvents.forEach(pe => {
      if (!passesTypeFilter('personal', pe.event_type)) return;
      push(pe.date, {
        kind: 'personal',
        id: pe.event_id,
        date: pe.date,
        time: pe.all_day ? '' : (pe.start_time || ''),
        end_time: pe.end_time || '',
        title: pe.title,
        description: pe.description || '',
        event_type: pe.event_type,
        priority: pe.priority || 'medium',
        completed: !!pe.completed,
        building_id: pe.building_id || null,
        building_name: pe.building_id ? (buildings.find(b => b.building_id === pe.building_id)?.name || '') : '',
        visibility: pe.visibility || 'private',
        all_day: !!pe.all_day,
        category: pe.category || '',
        raw: pe,
      });
    });
    return map;
  }, [events, personalEvents, buildings, passesTypeFilter, passesStatusFilter]);

  const summary = useMemo(() => {
    const today = ymd(new Date());
    const past = (events.meetings || []).filter(m => m.date <= today).sort((a, b) => b.date.localeCompare(a.date));
    const future = (events.meetings || []).filter(m => m.date > today).sort((a, b) => a.date.localeCompare(b.date));
    const nextMeetings = (events.next_meetings || []).slice().sort((a, b) => a.date.localeCompare(b.date));
    const nextMeetingDate = nextMeetings.length ? nextMeetings[0].date : (future.length ? future[0].date : '');
    return {
      lastMeetingDate: past.length ? past[0].date : '',
      nextMeetingDate,
      taskCount: (events.tasks || []).length + personalEvents.length,
    };
  }, [events, personalEvents]);

  const alerts = useMemo(() => {
    const today = ymd(new Date());
    const out = [];
    const all = [
      ...(events.meetings || []).map(m => ({ ...m })),
      ...(events.next_meetings || []).map(m => ({ ...m })),
    ];
    all.forEach(e => {
      const d = daysBetween(today, e.date);
      if (d !== null && d >= 0 && d <= 30) out.push({ ...e, daysLeft: d });
    });
    return out.sort((a, b) => a.daysLeft - b.daysLeft);
  }, [events]);

  const days = useMemo(() => {
    const arr = [];
    const todayStr = ymd(new Date());
    for (let i = 0; i < 42; i++) {
      const d = new Date(range.gridStart);
      d.setDate(range.gridStart.getDate() + i);
      const key = ymd(d);
      arr.push({
        date: d,
        key,
        inMonth: d.getMonth() === cursor.getMonth(),
        isToday: key === todayStr,
        events: grouped[key] || [],
      });
    }
    return arr;
  }, [range.gridStart, cursor, grouped]);

  const onPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const onNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const onToday = () => { const t0 = new Date(); setCursor(new Date(t0.getFullYear(), t0.getMonth(), 1)); };

  // -----------------------------------------------------------------
  // Legacy building-task dialog (unchanged behaviour, kept intact)
  // -----------------------------------------------------------------
  const openEditTask = async (task_id) => {
    try {
      const r = await axios.get(`${API}/tasks/${task_id}`);
      setTaskDialog({ mode: 'edit', task: r.data });
    } catch { toast.error('Task not found'); }
  };
  const saveTask = async () => {
    const t0 = taskDialog.task;
    if (!t0.building_id) { toast.error(language === 'el' ? 'Επιλέξτε πολυκατοικία' : 'Pick a building'); return; }
    if (!t0.start_date) { toast.error(language === 'el' ? 'Επιλέξτε ημερομηνία' : 'Pick a date'); return; }
    if (!(t0.title || t0.notes)) { toast.error(language === 'el' ? 'Συμπληρώστε τίτλο' : 'Title required'); return; }
    try {
      if (taskDialog.mode === 'create') {
        await axios.post(`${API}/tasks`, {
          building_id: t0.building_id, type: t0.type || 'building',
          title: t0.title || '', notes: t0.notes || '',
          start_date: t0.start_date, time: t0.time || null,
          assignee: t0.assignee || '', is_important: !!t0.is_important,
          status: t0.status || 'pending',
        });
        toast.success(language === 'el' ? 'Δημιουργήθηκε' : 'Created');
      } else {
        await axios.put(`${API}/tasks/${t0.task_id}`, {
          title: t0.title || '', notes: t0.notes || '',
          start_date: t0.start_date, time: t0.time || null,
          assignee: t0.assignee || '', is_important: !!t0.is_important,
          status: t0.status || 'pending',
        });
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      }
      setTaskDialog(null); fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const deleteTask = async (task_id) => {
    // Phase 810 — Actual deletion (called by the styled confirm dialog).
    try {
      await axios.delete(`${API}/tasks/${task_id}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setTaskDialog(null); setEventDialog(null); setDeleteConfirm(null); fetchEvents();
    } catch (e) {
      const detail = e.response?.data?.detail || (language === 'el' ? 'Η διαγραφή απέτυχε' : 'Delete failed');
      // Distinct message on permission errors so failure is never silent.
      if (e.response?.status === 403) toast.error(detail);
      else toast.error(detail);
    }
  };

  // -----------------------------------------------------------------
  // Phase 800 — Personal event dialog
  // -----------------------------------------------------------------
  const openCreatePersonal = (defaultDate = null) => {
    setPersonalDialog({
      mode: 'create',
      event: {
        event_type: 'personal',
        title: '', description: '',
        date: defaultDate || ymd(new Date()),
        all_day: true, start_time: '', end_time: '',
        priority: 'medium', category: '',
        completed: false,
        building_id: '',
        visibility: 'private',
        shared_with: [],
        reminder_minutes: null,
        repeat_rule: null,
      },
    });
  };
  const openEditPersonal = (raw) => setPersonalDialog({ mode: 'edit', event: { ...raw } });
  const savePersonal = async () => {
    const e0 = personalDialog.event;
    if (!e0.title.trim()) { toast.error(language === 'el' ? 'Απαιτείται τίτλος' : 'Title required'); return; }
    if (!e0.date) { toast.error(language === 'el' ? 'Απαιτείται ημερομηνία' : 'Date required'); return; }
    // Committee visibility requires a building.
    if (e0.visibility === 'committee' && !e0.building_id) {
      toast.error(language === 'el' ? 'Επιλέξτε πολυκατοικία για επιτροπή' : 'Pick a building for committee sharing');
      return;
    }
    const payload = {
      event_type: e0.event_type,
      title: e0.title.trim(),
      description: e0.description || '',
      date: e0.date,
      all_day: !!e0.all_day,
      start_time: e0.all_day ? '' : (e0.start_time || ''),
      end_time: e0.all_day ? '' : (e0.end_time || ''),
      priority: e0.priority || 'medium',
      category: e0.category || '',
      completed: !!e0.completed,
      building_id: e0.building_id || null,
      visibility: e0.visibility || 'private',
      shared_with: e0.shared_with || [],
      reminder_minutes: e0.reminder_minutes || null,
      repeat_rule: e0.repeat_rule || null,
    };
    try {
      if (personalDialog.mode === 'create') {
        await axios.post(`${API}/calendar/personal-events`, payload);
        toast.success(language === 'el' ? 'Δημιουργήθηκε' : 'Created');
      } else {
        await axios.put(`${API}/calendar/personal-events/${e0.event_id}`, payload);
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      }
      setPersonalDialog(null); setEventDialog(null); fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const deletePersonal = async (event_id) => {
    // Phase 810 — actual delete after styled confirm.
    try {
      await axios.delete(`${API}/calendar/personal-events/${event_id}`);
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setPersonalDialog(null); setEventDialog(null); setDeleteConfirm(null); fetchEvents();
    } catch (e) {
      const detail = e.response?.data?.detail || (language === 'el' ? 'Η διαγραφή απέτυχε' : 'Delete failed');
      toast.error(detail);
    }
  };
  const togglePersonalComplete = async (event_id) => {
    try {
      await axios.patch(`${API}/calendar/personal-events/${event_id}/complete`);
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // Pill colour for an event in the grid. Phase 943 — dynamic colour
  // per admin-configured kind. Personal events look up their `event_type`
  // in the admin catalogue; legacy tasks/meetings resolve to their
  // canonical kind_key (task/meeting/next_meeting). Falls back to the
  // legacy tailwind palette when the kind is not in the catalogue.
  const eventColor = (e) => {
    const key = e.kind === 'personal' ? e.event_type : e.kind;
    const dyn = kindByKey(key);
    if (dyn && dyn.color) {
      return { bg: '', text: 'text-white', dot: '', style: { backgroundColor: dyn.color, color: '#fff' }, dotStyle: { backgroundColor: dyn.color } };
    }
    // Legacy fallback.
    if (e.kind === 'meeting') return legacyMeetingColor;
    if (e.kind === 'next_meeting') return legacyNextMeetingColor;
    if (e.kind === 'task') return legacyTaskColor;
    if (e.kind === 'personal') return typeByKey(e.event_type) || typeByKey('personal');
    return { bg: 'bg-zinc-300', text: 'text-zinc-900', dot: 'bg-zinc-300' };
  };

  const typeLabel = (tp) => {
    const meta = typeByKey(tp);
    if (!meta) return tp;
    return language === 'el' ? meta.label_el : meta.label_en;
  };
  const priorityLabel = (p) => {
    const m = PRIORITY_META[p];
    return m ? (language === 'el' ? m.label_el : m.label_en) : p;
  };

  return (
    <Layout>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="calendar-header">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {language === 'el' ? 'Ημερολόγιο' : 'Calendar'}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {language === 'el'
                ? 'Συνελεύσεις, tasks, προσωπικά events και υπενθυμίσεις σε μια οθόνη'
                : 'Meetings, tasks, personal events and reminders in one view'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onToday} className="rounded-none" data-testid="cal-today-btn">
              {language === 'el' ? 'Σήμερα' : 'Today'}
            </Button>
            <button onClick={onPrev} className="p-2 border border-zinc-200 hover:bg-zinc-50" data-testid="cal-prev-btn"><CaretLeft size={16} /></button>
            <div className="px-3 py-1.5 text-sm font-semibold min-w-[180px] text-center border border-zinc-200" data-testid="cal-month-label">
              {monthNames[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button onClick={onNext} className="p-2 border border-zinc-200 hover:bg-zinc-50" data-testid="cal-next-btn"><CaretRight size={16} /></button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // Phase 811 — Open Print Calendar dialog. Preload preview.
                // Seeds `event_types` from active calendar filters (if any),
                // so "Print" respects whatever the user is looking at.
                const initial = {
                  format: 'pdf',
                  layout: 'task_list',
                  scope: 'all',
                  status: 'all',
                  priority: 'all',
                  date_range: 'month',
                  event_types: activeTypeFilters.size ? Array.from(activeTypeFilters) : null,
                  title: language === 'el' ? 'Ημερολόγιο' : 'Calendar',
                };
                setPrintDialog(initial);
                try {
                  const r = await axios.post(`${API}/calendar/tasks/export/preview`, initial);
                  setPrintPreview(r.data);
                } catch { setPrintPreview({ count: 0, range: {}, estimated_pages: 1, filters: {} }); }
              }}
              className="rounded-none"
              data-testid="cal-print-btn"
            >
              <Printer size={13} className="mr-1" /> {language === 'el' ? 'Εκτύπωση' : 'Print'}
            </Button>
            <Button onClick={() => openCreatePersonal()} className="bg-zinc-950 rounded-none" size="sm" data-testid="cal-add-event-btn">
              <Plus size={14} className="mr-1" /> {language === 'el' ? 'Νέο Event' : 'Add Event'}
            </Button>
            {/* Phase 1018 — "Tasks Management" button REMOVED from
                Calendar. Task Management is now its own top-level
                module (see Layout.js top-nav). Calendar and Task
                Management are strictly separate surfaces. */}
            {/* Phase 943 — Admin-only category management. Opens the
                CategoryManager dialog bound to /calendar/event-kinds. */}
            <Button onClick={() => setCategoryMgrOpen(true)} variant="outline" size="sm" className="rounded-none" data-testid="cal-categories-btn">
              <Palette size={13} className="mr-1" /> {language === 'el' ? 'Κατηγορίες' : 'Categories'}
            </Button>
          </div>
        </div>

        {/* Phase 1017 — Building-derived summary cards ("Last Meeting",
            "Next Meeting") REMOVED. Calendar is a standalone module —
            only shows Events This Month (personal events). */}
        <div className="grid grid-cols-1 gap-3" data-testid="cal-summary">
          <div className="bg-white border border-zinc-200 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks size={14} className="text-emerald-600" />
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                {language === 'el' ? 'Events Μήνα' : 'Events This Month'}
              </span>
            </div>
            <div className="text-lg font-semibold" data-testid="cal-task-count">{summary.taskCount}</div>
          </div>
        </div>

        {/* Phase 1017 — 30-day "Upcoming Meetings" alerts REMOVED (used
            to aggregate building-sourced meetings). */}

        {/* ============================================================
            PHASE 800 / 943 — TYPE FILTER CHIPS (admin-editable) +
            STATUS FILTER (Active/Completed/Cancelled/All).
            ============================================================ */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="cal-type-filters">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1">
            {language === 'el' ? 'Κατηγορίες' : 'Categories'}
          </span>
          {kindsForUi.map(tp => {
            const isActive = activeTypeFilters.has(tp.key);
            const legacyClass = tp._legacy ? tp._legacy.bg + ' ' + tp._legacy.text : '';
            const style = tp.color
              ? (isActive
                  ? { backgroundColor: tp.color, color: '#fff', borderColor: tp.color }
                  : { color: tp.color, borderColor: tp.color + '55' })
              : undefined;
            return (
              <button
                key={tp.key}
                type="button"
                onClick={() => toggleTypeFilter(tp.key)}
                aria-pressed={isActive}
                style={style}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border transition-colors ${!tp.color && isActive ? `${legacyClass} border-transparent` : ''} ${!tp.color && !isActive ? 'border-zinc-200 text-zinc-600 hover:bg-zinc-50' : ''}`}
                data-testid={`cal-filter-${tp.key}`}
              >
                {tp.icon
                  ? <CategoryIcon name={tp.icon} size={12} />
                  : tp._legacy ? <tp._legacy.Icon size={12} weight="bold" /> : null}
                {tp.name}
              </button>
            );
          })}
          {activeTypeFilters.size > 0 && (
            <button type="button" onClick={clearTypeFilters} className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-red-600 ml-1" data-testid="cal-filter-clear">
              × {language === 'el' ? 'Καθαρισμός' : 'Clear'}
            </button>
          )}
        </div>

        {/* Phase 943 — Status filter row. Defaults to "Active" which
            hides completed + cancelled events (so the calendar stays
            clean). Users can flip to any status to review history. */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="cal-status-filters">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 mr-1">
            {language === 'el' ? 'Κατάσταση' : 'Status'}
          </span>
          {[
            { k: 'active',    el: 'Ενεργές',       en: 'Active',    color: '#10b981' },
            { k: 'completed', el: 'Ολοκληρωμένες', en: 'Completed', color: '#3b82f6' },
            { k: 'cancelled', el: 'Ακυρωμένες',    en: 'Cancelled', color: '#a1a1aa' },
            { k: 'all',       el: 'Όλες',          en: 'All',       color: '#525252' },
          ].map(opt => {
            const isOn = statusFilter === opt.k;
            return (
              <button
                key={opt.k}
                type="button"
                onClick={() => setStatusFilter(opt.k)}
                aria-pressed={isOn}
                style={isOn ? { backgroundColor: opt.color, color: '#fff', borderColor: opt.color } : undefined}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border transition-colors ${isOn ? '' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
                data-testid={`cal-status-${opt.k}`}
              >
                {language === 'el' ? opt.el : opt.en}
              </button>
            );
          })}
        </div>

        {/* Legend. Phase 943 — dynamic from admin catalogue. */}
        <div className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap">
          {kindsForUi.map(tp => (
            <div key={tp.key} className="flex items-center gap-1.5">
              <span
                className={`inline-block w-3 h-3 ${!tp.color && tp._legacy ? tp._legacy.bg : ''}`}
                style={tp.color ? { backgroundColor: tp.color } : undefined}
              />
              {tp.name}
            </div>
          ))}
        </div>

        {/* Month grid */}
        <div className="bg-white border border-zinc-200" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
            {weekdays.map(d => (
              <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 text-center border-r last:border-r-0 border-zinc-200">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day, i) => {
              const isLastRow = i >= 35;
              return (
                <div
                  key={day.key}
                  className={`min-h-[110px] p-1.5 border-r border-zinc-200 ${isLastRow ? '' : 'border-b'} ${day.inMonth ? 'bg-white' : 'bg-zinc-50/50'} ${(i + 1) % 7 === 0 ? 'border-r-0' : ''}`}
                  data-testid={`cal-day-${day.key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-mono font-semibold ${day.isToday ? 'bg-zinc-900 text-white px-1.5 py-0.5' : day.inMonth ? 'text-zinc-900' : 'text-zinc-300'}`}>
                      {day.date.getDate()}
                    </span>
                    {day.inMonth && (
                      <button
                        onClick={() => openCreatePersonal(day.key)}
                        className="text-zinc-300 hover:text-zinc-900 transition-opacity"
                        title={language === 'el' ? 'Νέο Event' : 'Add Event'}
                        data-testid={`cal-add-on-${day.key}`}
                      ><Plus size={12} /></button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {day.events.slice(0, 3).map((e, idx) => {
                      const col = eventColor(e);
                      const prioDot = e.kind === 'personal' ? PRIORITY_META[e.priority || 'medium']?.dot : null;
                      const isDone = !!e.completed;
                      return (
                        <button
                          key={`${e.kind}-${e.id}-${idx}`}
                          onClick={() => {
                            if (e.kind === 'task') openEditTask(e.id);
                            else if (e.kind === 'personal') setEventDialog(e);
                            else setEventDialog(e);
                          }}
                          style={col.style}
                          className={`w-full text-left px-1.5 py-0.5 text-[10px] truncate ${col.bg} ${col.text} hover:opacity-90 flex items-center gap-1 ${isDone ? 'line-through opacity-60' : ''}`}
                          title={e.title}
                          data-testid={`cal-evt-${e.kind}-${e.id}`}
                        >
                          {prioDot && <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${prioDot}`}></span>}
                          <span className="truncate">{e.time ? `${e.time} ` : ''}{e.title}</span>
                        </button>
                      );
                    })}
                    {day.events.length > 3 && (
                      // Phase 812 — Clickable "+X more" chip. Opens the
                      // full day-events modal so no event is hidden.
                      <button
                        type="button"
                        onClick={() => setDayEventsDialog({ key: day.key, date: day.date, events: day.events })}
                        className="w-full text-left text-[10px] text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 px-1 py-0.5 border border-transparent hover:border-zinc-200 transition-colors font-medium"
                        data-testid={`cal-more-${day.key}`}
                        title={language === 'el' ? 'Προβολή όλων' : 'View all events'}
                      >
                        +{day.events.length - 3} {language === 'el' ? 'ακόμη' : 'more'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legacy task create/edit dialog (unchanged) */}
      <Dialog open={!!taskDialog} onOpenChange={(v) => !v && setTaskDialog(null)}>
        <DialogContent className="sm:max-w-[520px] rounded-none">
          <DialogHeader>
            <DialogTitle>
              {taskDialog?.mode === 'edit'
                ? (language === 'el' ? 'Επεξεργασία Task' : 'Edit Task')
                : (language === 'el' ? 'Νέο Task' : 'New Task')}
            </DialogTitle>
          </DialogHeader>
          {taskDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{language === 'el' ? 'Πολυκατοικία' : 'Building'}</Label>
                <select
                  value={taskDialog.task.building_id || ''}
                  onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, building_id: e.target.value } })}
                  disabled={taskDialog.mode === 'edit'}
                  className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
                  data-testid="task-building-select"
                >
                  <option value="">—</option>
                  {buildings.map(b => (<option key={b.building_id} value={b.building_id}>{b.name}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Τίτλος' : 'Title'}</Label>
                <Input value={taskDialog.task.title || ''} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, title: e.target.value } })} className="mt-1 rounded-none" data-testid="task-title-input" />
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Περιγραφή' : 'Description'}</Label>
                <Textarea value={taskDialog.task.notes || ''} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, notes: e.target.value } })} rows={3} className="mt-1 rounded-none text-sm" data-testid="task-notes-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ημερομηνία' : 'Date'}</Label>
                  <DatePickerDMY value={taskDialog.task.start_date || ''} onChange={(v) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, start_date: v } })} data-testid="task-date-input" />
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ώρα (προαιρετικό)' : 'Time (optional)'}</Label>
                  <Input type="time" value={taskDialog.task.time || ''} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, time: e.target.value } })} className="mt-1 rounded-none" data-testid="task-time-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Ανάθεση (προαιρετικό)' : 'Assignee (optional)'}</Label>
                <Input value={taskDialog.task.assignee || ''} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, assignee: e.target.value } })} className="mt-1 rounded-none" data-testid="task-assignee-input" />
              </div>
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={!!taskDialog.task.is_important} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, is_important: e.target.checked } })} data-testid="task-important-cb" />
                  <span className="text-xs">{language === 'el' ? 'Σημαντικό' : 'Important'}</span>
                </label>
                <select value={taskDialog.task.status || 'pending'} onChange={(e) => setTaskDialog({ ...taskDialog, task: { ...taskDialog.task, status: e.target.value } })} className="border border-zinc-300 px-2 py-1 text-xs rounded-none bg-white" data-testid="task-status-select">
                  <option value="pending">{language === 'el' ? 'Σε εκκρεμότητα' : 'Pending'}</option>
                  <option value="in_progress">{language === 'el' ? 'Σε εξέλιξη' : 'In Progress'}</option>
                  <option value="completed">{language === 'el' ? 'Ολοκληρώθηκε' : 'Completed'}</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {taskDialog?.mode === 'edit' && (
              <Button variant="outline" onClick={() => setDeleteConfirm({ kind: 'task', id: taskDialog.task.task_id, title: taskDialog.task.title || (language === 'el' ? 'εργασία' : 'task') })} className="rounded-none mr-auto text-red-600" data-testid="task-delete-btn">
                {language === 'el' ? 'Διαγραφή' : 'Delete'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setTaskDialog(null)} className="rounded-none">
              {language === 'el' ? 'Άκυρο' : 'Cancel'}
            </Button>
            <Button onClick={saveTask} className="bg-zinc-950 rounded-none" data-testid="task-save-btn">
              {language === 'el' ? 'Αποθήκευση' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          PHASE 800 — PERSONAL EVENT dialog (create/edit)
          ============================================================ */}
      <Dialog open={!!personalDialog} onOpenChange={(v) => !v && setPersonalDialog(null)}>
        <DialogContent className="sm:max-w-[640px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {personalDialog?.mode === 'edit'
                ? (language === 'el' ? 'Επεξεργασία Event' : 'Edit Event')
                : (language === 'el' ? 'Νέο Event' : 'New Event')}
            </DialogTitle>
          </DialogHeader>
          {personalDialog && (
            <div className="space-y-3">
              {/* Type selector — Phase 943 dynamic kinds. */}
              <div>
                <Label className="text-xs">{language === 'el' ? 'Τύπος' : 'Type'}</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1" data-testid="pe-type-grid">
                  {kindsForUi.filter(tp => !['meeting', 'next_meeting', 'task'].includes(tp.key)).map(tp => {
                    const isActive = personalDialog.event.event_type === tp.key;
                    const legacyClass = tp._legacy ? `${tp._legacy.bg} ${tp._legacy.text}` : '';
                    const style = tp.color
                      ? (isActive
                          ? { backgroundColor: tp.color, color: '#fff', borderColor: tp.color }
                          : { borderColor: tp.color + '55', color: tp.color })
                      : undefined;
                    return (
                      <button
                        key={tp.key} type="button"
                        onClick={() => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, event_type: tp.key } })}
                        style={style}
                        className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs border transition-colors ${!tp.color && isActive ? `${legacyClass} border-transparent` : ''} ${!tp.color && !isActive ? 'border-zinc-200 hover:bg-zinc-50' : ''}`}
                        data-testid={`pe-type-${tp.key}`}
                      >
                        {tp.icon
                          ? <CategoryIcon name={tp.icon} size={13} />
                          : tp._legacy ? <tp._legacy.Icon size={13} weight="bold" /> : null}
                        {tp.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs">{language === 'el' ? 'Τίτλος' : 'Title'}</Label>
                <Input value={personalDialog.event.title} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, title: e.target.value } })} className="mt-1 rounded-none" data-testid="pe-title" />
              </div>

              <div>
                <Label className="text-xs">{language === 'el' ? 'Περιγραφή' : 'Description'}</Label>
                <Textarea value={personalDialog.event.description || ''} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, description: e.target.value } })} rows={2} className="mt-1 rounded-none text-sm" data-testid="pe-description" />
              </div>

              {/* Date + all_day + start/end time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ημερομηνία' : 'Date'}</Label>
                  <DatePickerDMY value={personalDialog.event.date} onChange={v => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, date: v } })} data-testid="pe-date" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!personalDialog.event.all_day} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, all_day: e.target.checked } })} data-testid="pe-allday" />
                    <span>{language === 'el' ? 'Όλη μέρα' : 'All day'}</span>
                  </label>
                </div>
              </div>
              {!personalDialog.event.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{language === 'el' ? 'Έναρξη' : 'Start'}</Label>
                    <Input type="time" value={personalDialog.event.start_time || ''} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, start_time: e.target.value } })} className="mt-1 rounded-none" data-testid="pe-start" />
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'el' ? 'Λήξη' : 'End'}</Label>
                    <Input type="time" value={personalDialog.event.end_time || ''} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, end_time: e.target.value } })} className="mt-1 rounded-none" data-testid="pe-end" />
                  </div>
                </div>
              )}

              {/* Priority + Category */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Προτεραιότητα' : 'Priority'}</Label>
                  <div className="flex gap-1 mt-1" data-testid="pe-priority-row">
                    {['high', 'medium', 'low'].map(p => {
                      const isActive = personalDialog.event.priority === p;
                      const meta = PRIORITY_META[p];
                      return (
                        <button
                          key={p} type="button"
                          onClick={() => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, priority: p } })}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs border transition-colors ${isActive ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                          data-testid={`pe-priority-${p}`}
                        >
                          <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`}></span>
                          {priorityLabel(p)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Κατηγορία (προαιρετικό)' : 'Category (optional)'}</Label>
                  <Input value={personalDialog.event.category || ''} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, category: e.target.value } })} className="mt-1 rounded-none" data-testid="pe-category" />
                </div>
              </div>

              {/* Building (optional) */}
              <div>
                <Label className="text-xs">
                  {language === 'el' ? 'Πολυκατοικία (προαιρετικό)' : 'Building (optional)'}
                </Label>
                <select
                  value={personalDialog.event.building_id || ''}
                  onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, building_id: e.target.value } })}
                  className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
                  data-testid="pe-building"
                >
                  <option value="">—</option>
                  {buildings.map(b => (<option key={b.building_id} value={b.building_id}>{b.name}</option>))}
                </select>
              </div>

              {/* Visibility */}
              <div>
                <Label className="text-xs">{language === 'el' ? 'Ορατότητα' : 'Visibility'}</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1" data-testid="pe-visibility-row">
                  {VISIBILITY_META.map(v => {
                    const Icon = v.Icon;
                    const isActive = (personalDialog.event.visibility || 'private') === v.key;
                    return (
                      <button
                        key={v.key} type="button"
                        onClick={() => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, visibility: v.key } })}
                        className={`inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs border transition-colors ${isActive ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`pe-visibility-${v.key}`}
                      >
                        <Icon size={13} weight="bold" />
                        {language === 'el' ? v.label_el : v.label_en}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Completed */}
              {(personalDialog.event.event_type === 'task' || personalDialog.event.event_type === 'personal') && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="pe-completed-row">
                  <input type="checkbox" checked={!!personalDialog.event.completed} onChange={e => setPersonalDialog({ ...personalDialog, event: { ...personalDialog.event, completed: e.target.checked } })} data-testid="pe-completed" />
                  <span>{language === 'el' ? 'Ολοκληρώθηκε' : 'Completed'}</span>
                </label>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {personalDialog?.mode === 'edit' && (
              <Button variant="outline" onClick={() => setDeleteConfirm({ kind: 'personal', id: personalDialog.event.event_id, title: personalDialog.event.title || (language === 'el' ? 'event' : 'event') })} className="rounded-none mr-auto text-red-600" data-testid="pe-delete">
                <Trash size={13} className="mr-1" />{language === 'el' ? 'Διαγραφή' : 'Delete'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setPersonalDialog(null)} className="rounded-none">
              {language === 'el' ? 'Άκυρο' : 'Cancel'}
            </Button>
            <Button onClick={savePersonal} className="bg-zinc-950 rounded-none" data-testid="pe-save">
              {language === 'el' ? 'Αποθήκευση' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event detail dialog (meetings + personal events) */}
      <Dialog open={!!eventDialog} onOpenChange={(v) => !v && setEventDialog(null)}>
        <DialogContent className="sm:max-w-[520px] rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {eventDialog?.kind === 'next_meeting' && (language === 'el' ? 'Επόμενη Συνέλευση' : 'Next Meeting')}
              {eventDialog?.kind === 'meeting' && (language === 'el' ? 'Συνέλευση' : 'Meeting')}
              {eventDialog?.kind === 'personal' && (
                <>
                  <span className={`inline-block w-2.5 h-2.5 ${typeByKey(eventDialog.event_type)?.dot || 'bg-zinc-400'}`}></span>
                  {typeLabel(eventDialog.event_type)}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {eventDialog && (
            <div className="space-y-2 text-sm">
              <div className="font-semibold text-base">{eventDialog.title}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <CalendarBlank size={14} className="text-zinc-500" />
                <span className="font-mono">{fmtDmy(eventDialog.date)}</span>
                {eventDialog.time && (
                  <>
                    <Clock size={14} className="text-zinc-500 ml-2" />
                    <span className="font-mono">{eventDialog.time}{eventDialog.end_time ? `–${eventDialog.end_time}` : ''}</span>
                  </>
                )}
                {eventDialog.kind === 'personal' && eventDialog.all_day && (
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-200 px-1.5">
                    {language === 'el' ? 'Όλη μέρα' : 'All day'}
                  </span>
                )}
              </div>
              {eventDialog.building_name && (
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-zinc-500" />
                  <span>{eventDialog.building_name}</span>
                </div>
              )}
              {/* Phase 832 — Show full Meeting Type + Status inside the
                  Calendar event dialog so managers can identify each
                  meeting without opening the record. */}
              {(eventDialog.kind === 'meeting' || eventDialog.kind === 'next_meeting') && (eventDialog.meeting_type_label || eventDialog.meeting_type) && (
                <div className="flex items-center gap-2 text-zinc-700">
                  <Buildings size={14} className="text-zinc-500" />
                  <span data-testid="event-meeting-type">
                    {eventDialog.meeting_type_label || eventDialog.meeting_type}
                  </span>
                </div>
              )}
              {(eventDialog.kind === 'meeting' || eventDialog.kind === 'next_meeting') && eventDialog.status && (
                <div className="flex items-center gap-2 text-zinc-700">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500 border border-zinc-200 px-1.5 py-0.5" data-testid="event-meeting-status">
                    {eventDialog.status}
                  </span>
                </div>
              )}
              {eventDialog.location && (
                <div className="flex items-center gap-2"><MapPin size={14} className="text-zinc-500" /><span>{eventDialog.location}</span></div>
              )}
              {eventDialog.kind === 'personal' && (
                <>
                  {eventDialog.description && (
                    <div className="text-zinc-700 whitespace-pre-line border-l-2 border-zinc-200 pl-2 text-[13px]">
                      {eventDialog.description}
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs text-zinc-600 pt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${PRIORITY_META[eventDialog.priority]?.dot || 'bg-zinc-300'}`}></span>
                      {priorityLabel(eventDialog.priority)}
                    </span>
                    {eventDialog.category && <span className="text-zinc-500">· {eventDialog.category}</span>}
                    <span className="text-zinc-500 inline-flex items-center gap-1">
                      {eventDialog.visibility === 'private' && <><Lock size={11} />{language === 'el' ? 'Ιδιωτικό' : 'Private'}</>}
                      {eventDialog.visibility === 'team' && <><UsersThree size={11} />{language === 'el' ? 'Ομάδα' : 'Team'}</>}
                      {eventDialog.visibility === 'committee' && <><Buildings size={11} />{language === 'el' ? 'Επιτροπή' : 'Committee'}</>}
                    </span>
                  </div>
                </>
              )}
              {typeof eventDialog.quorum_percentage === 'number' && (
                <div className="text-xs text-zinc-500">
                  {language === 'el' ? 'Απαρτία:' : 'Quorum:'} {eventDialog.quorum_percentage.toFixed(2)}%
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            {eventDialog?.kind === 'personal' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => togglePersonalComplete(eventDialog.id)}
                  className="rounded-none mr-auto"
                  data-testid="pe-toggle-complete"
                >
                  {eventDialog.completed
                    ? <><Square size={13} className="mr-1" />{language === 'el' ? 'Ακύρωση Ολοκλήρωσης' : 'Mark Incomplete'}</>
                    : <><CheckSquare size={13} className="mr-1" />{language === 'el' ? 'Ολοκληρώθηκε' : 'Mark Complete'}</>
                  }
                </Button>
                <Button variant="outline" onClick={() => { openEditPersonal(eventDialog.raw); }} className="rounded-none" data-testid="pe-open-edit">
                  {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setEventDialog(null)} className="rounded-none">
              {language === 'el' ? 'Κλείσιμο' : 'Close'}
            </Button>
            {eventDialog?.building_id && (eventDialog.kind === 'meeting' || eventDialog.kind === 'next_meeting') && (
              <Button onClick={() => navigate(`/buildings/${eventDialog.building_id}?tab=meetings`)} className="bg-zinc-950 rounded-none" data-testid="event-open-btn">
                {language === 'el' ? 'Άνοιγμα' : 'Open'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ============================================================
          PHASE 812 — DAY EVENTS modal ("+X more" opener)
          Shows ALL events for the clicked day, with per-event actions.
          Events are read live from `grouped[key]` so mutations from
          child dialogs (edit / delete / complete) reflect immediately.
          ============================================================ */}
      <Dialog open={!!dayEventsDialog} onOpenChange={(v) => !v && setDayEventsDialog(null)}>
        <DialogContent className="sm:max-w-[640px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CalendarBlank size={18} />
                {language === 'el' ? 'Events' : 'Events'} — {dayEventsDialog?.date && (
                  <span className="font-mono text-base">
                    {String(dayEventsDialog.date.getDate()).padStart(2, '0')}/
                    {String(dayEventsDialog.date.getMonth() + 1).padStart(2, '0')}/
                    {dayEventsDialog.date.getFullYear()}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const d = dayEventsDialog?.key;
                  setDayEventsDialog(null);
                  openCreatePersonal(d);
                }}
                className="bg-zinc-950 rounded-none"
                data-testid="day-events-add-btn"
              >
                <Plus size={13} className="mr-1" /> {language === 'el' ? 'Νέο Event' : 'Add Event'}
              </Button>
            </DialogTitle>
          </DialogHeader>
          {dayEventsDialog && (() => {
            // Read the LIVE list from grouped[] so mutations reflect.
            const liveEvents = grouped[dayEventsDialog.key] || dayEventsDialog.events || [];
            if (liveEvents.length === 0) {
              return (
                <div className="py-10 text-center text-sm text-zinc-500">
                  {language === 'el' ? 'Δεν υπάρχουν πλέον events για αυτή την ημέρα.' : 'No events remain for this day.'}
                </div>
              );
            }
            return (
              <div className="space-y-2" data-testid="day-events-list">
                {liveEvents.map((e, idx) => {
                  const col = eventColor(e);
                  const prioDot = e.kind === 'personal' ? PRIORITY_META[e.priority || 'medium']?.dot : null;
                  const isDone = !!e.completed;
                  const evtKindLabel =
                    e.kind === 'meeting' ? (language === 'el' ? 'Συνέλευση' : 'Meeting')
                    : e.kind === 'next_meeting' ? (language === 'el' ? 'Επόμενη Συνέλευση' : 'Next Meeting')
                    : e.kind === 'task' ? (language === 'el' ? 'Task Πολυκατοικίας' : 'Building Task')
                    : typeLabel(e.event_type);
                  return (
                    <div
                      key={`${e.kind}-${e.id}-${idx}`}
                      className={`border border-zinc-200 hover:border-zinc-400 transition-colors ${isDone ? 'opacity-60' : ''}`}
                      data-testid={`day-event-card-${e.kind}-${e.id}`}
                    >
                      <div className="flex items-stretch">
                        {/* Coloured left rail */}
                        <div className={`w-1.5 ${col.bg}`} style={col.style} aria-hidden="true"></div>
                        <div className="flex-1 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${col.bg} ${col.text}`} style={col.style}>
                                  {evtKindLabel}
                                </span>
                                {prioDot && (
                                  <span className={`inline-flex items-center gap-1 text-[10px] text-zinc-500`}>
                                    <span className={`inline-block w-2 h-2 rounded-full ${prioDot}`}></span>
                                    {priorityLabel(e.priority)}
                                  </span>
                                )}
                                {e.time && (
                                  <span className="text-[11px] font-mono text-zinc-600 inline-flex items-center gap-0.5">
                                    <Clock size={11} /> {e.time}{e.end_time ? `–${e.end_time}` : ''}
                                  </span>
                                )}
                                {e.kind === 'personal' && e.all_day && !e.time && (
                                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-200 px-1.5">
                                    {language === 'el' ? 'Όλη μέρα' : 'All day'}
                                  </span>
                                )}
                              </div>
                              <div className={`text-sm font-semibold text-zinc-900 truncate ${isDone ? 'line-through' : ''}`} title={e.title}>
                                {e.title}
                              </div>
                              {e.description && (
                                <div className="text-xs text-zinc-600 line-clamp-2 mt-0.5">{e.description}</div>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500 flex-wrap">
                                {e.building_name && (
                                  <span className="inline-flex items-center gap-1"><Buildings size={10} />{e.building_name}</span>
                                )}
                                {e.category && (
                                  <span>· {e.category}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Actions */}
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            {/* Mark complete — Task / Personal task-like */}
                            {(e.kind === 'personal' && (e.event_type === 'task' || e.event_type === 'personal')) && (
                              <button
                                type="button"
                                onClick={() => togglePersonalComplete(e.id)}
                                className="text-[11px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                                data-testid={`day-event-complete-${e.id}`}
                              >
                                {e.completed
                                  ? <><Square size={11} />{language === 'el' ? 'Ακύρωση Ολ.' : 'Undo'}</>
                                  : <><CheckSquare size={11} />{language === 'el' ? 'Ολοκλήρωση' : 'Complete'}</>}
                              </button>
                            )}
                            {/* Open Related Building */}
                            {e.building_id && (
                              <button
                                type="button"
                                onClick={() => { setDayEventsDialog(null); navigate(`/buildings/${e.building_id}${e.kind === 'meeting' || e.kind === 'next_meeting' ? '?tab=meetings' : ''}`); }}
                                className="text-[11px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                                data-testid={`day-event-openbld-${e.id}`}
                              >
                                <Buildings size={11} />{language === 'el' ? 'Πολυκατοικία' : 'Open Building'}
                              </button>
                            )}
                            {/* View — non-mutating detail */}
                            <button
                              type="button"
                              onClick={() => {
                                if (e.kind === 'personal') setEventDialog(e);
                                else if (e.kind === 'task') openEditTask(e.id);
                                else setEventDialog(e);
                              }}
                              className="text-[11px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                              data-testid={`day-event-view-${e.id}`}
                            >
                              {language === 'el' ? 'Προβολή' : 'View'}
                            </button>
                            {/* Edit */}
                            {e.kind === 'personal' && (
                              <button
                                type="button"
                                onClick={() => { setDayEventsDialog(null); openEditPersonal(e.raw); }}
                                className="text-[11px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                                data-testid={`day-event-edit-${e.id}`}
                              >
                                {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                              </button>
                            )}
                            {e.kind === 'task' && (
                              <button
                                type="button"
                                onClick={() => { setDayEventsDialog(null); openEditTask(e.id); }}
                                className="text-[11px] px-2 py-1 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                                data-testid={`day-event-edit-${e.id}`}
                              >
                                {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                              </button>
                            )}
                            {/* Delete — only for user-owned / task rows */}
                            {(e.kind === 'personal' || e.kind === 'task') && (
                              <button
                                type="button"
                                onClick={() => setDeleteConfirm({
                                  kind: e.kind === 'personal' ? 'personal' : 'task',
                                  id: e.id,
                                  title: e.title || '',
                                })}
                                className="text-[11px] px-2 py-1 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                                data-testid={`day-event-delete-${e.id}`}
                              >
                                <Trash size={11} />{language === 'el' ? 'Διαγραφή' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDayEventsDialog(null)} className="rounded-none" data-testid="day-events-close">
              {language === 'el' ? 'Κλείσιμο' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          PHASE 810 — DELETE CONFIRMATION dialog (replaces window.confirm)
          ============================================================ */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-[440px] rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={18} weight="fill" />
              {language === 'el' ? 'Επιβεβαίωση Διαγραφής' : 'Confirm Deletion'}
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <div className="text-sm text-zinc-700 py-2">
              <p>{language === 'el'
                ? 'Είσαι βέβαιος/η ότι θέλεις να διαγράψεις αυτή την εργασία;'
                : 'Are you sure you want to delete this task?'}</p>
              <p className="mt-2 text-zinc-500 font-mono text-xs border-l-2 border-zinc-200 pl-2 italic">
                &ldquo;{deleteConfirm.title}&rdquo;
              </p>
              <p className="mt-3 text-[11px] text-zinc-500">
                {language === 'el'
                  ? 'Δεν μπορείς να την αναιρέσεις.'
                  : 'This action cannot be undone.'}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-none" data-testid="delete-cancel">
              {language === 'el' ? 'Ακύρωση' : 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                if (!deleteConfirm) return;
                if (deleteConfirm.kind === 'personal') deletePersonal(deleteConfirm.id);
                else deleteTask(deleteConfirm.id);
              }}
              className="bg-red-600 hover:bg-red-700 rounded-none text-white"
              data-testid="delete-confirm"
            >
              <Trash size={13} className="mr-1" />{language === 'el' ? 'Διαγραφή' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          PHASE 811 — PRINT CALENDAR dialog (PDF only, multi-select types)
          ============================================================ */}
      <Dialog open={!!printDialog} onOpenChange={(v) => { if (!v) { setPrintDialog(null); setPrintPreview(null); } }}>
        <DialogContent className="sm:max-w-[720px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer size={18} weight="bold" />
              {language === 'el' ? 'Εκτύπωση Ημερολογίου' : 'Print Calendar'}
            </DialogTitle>
          </DialogHeader>
          {printDialog && (() => {
            // Refresh preview each time filters change. Helper closure.
            const refreshPreview = async (next) => {
              setPrintDialog(next);
              try {
                const r = await axios.post(`${API}/calendar/tasks/export/preview`, next);
                setPrintPreview(r.data);
              } catch { /* noop */ }
            };
            const selectedTypes = printDialog.event_types || [];
            const toggleType = async (key) => {
              const setNext = new Set(selectedTypes);
              if (setNext.has(key)) setNext.delete(key); else setNext.add(key);
              const arr = Array.from(setNext);
              await refreshPreview({ ...printDialog, event_types: arr.length ? arr : null });
            };
            const allTypesSelected = selectedTypes.length === 0;

            // 10 filter options (9 event types + a Meetings row for legacy db.meetings).
            const PRINT_TYPES = [
              { key: 'building',    label_en: 'Building',    label_el: 'Πολυκατοικία', Icon: Buildings },
              { key: 'personal',    label_en: 'Personal',    label_el: 'Προσωπικό',    Icon: UserIcon },
              { key: 'task',        label_en: 'Tasks',       label_el: 'Εργασίες',     Icon: ListChecks },
              { key: 'appointment', label_en: 'Appointments',label_el: 'Ραντεβού',     Icon: Briefcase },
              { key: 'meeting',     label_en: 'Meetings',    label_el: 'Συνελεύσεις',  Icon: Users },
              { key: 'finance',     label_en: 'Finance',     label_el: 'Οικονομικά',   Icon: CurrencyCircleDollar },
              { key: 'call',        label_en: 'Calls',       label_el: 'Κλήσεις',      Icon: Phone },
              { key: 'vehicle',     label_en: 'Vehicle',     label_el: 'Όχημα',        Icon: Car },
              { key: 'birthday',    label_en: 'Birthdays',   label_el: 'Γενέθλια',     Icon: Cake },
              { key: 'note',        label_en: 'Notes',       label_el: 'Σημειώσεις',   Icon: Note },
            ];

            return (
              <div className="space-y-3">
                {/* Live preview banner */}
                <div className="bg-zinc-50 border border-zinc-200 px-3 py-2.5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="print-preview-banner">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {language === 'el' ? 'Σύνολο Events' : 'Total Events'}
                    </div>
                    <div className="text-xl font-bold font-mono" data-testid="print-count">{printPreview?.count ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {language === 'el' ? 'Επιλεγμένοι Τύποι' : 'Selected Types'}
                    </div>
                    <div className="text-xl font-bold font-mono">
                      {allTypesSelected
                        ? (language === 'el' ? 'Όλοι' : 'All')
                        : selectedTypes.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {language === 'el' ? 'Εύρος Ημερομηνιών' : 'Date Range'}
                    </div>
                    <div className="text-xs font-mono mt-1">
                      {printPreview?.range?.start
                        ? `${printPreview.range.start} → ${printPreview.range.end || '—'}`
                        : (language === 'el' ? 'Όλα' : 'All')}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {language === 'el' ? 'Εκτ. Σελίδες' : 'Estimated Pages'}
                    </div>
                    <div className="text-xl font-bold font-mono">~{printPreview?.estimated_pages ?? 1}</div>
                  </div>
                </div>

                {/* Multi-select event types */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">{language === 'el' ? 'Τύποι Events' : 'Event Types'}</Label>
                    <button
                      type="button"
                      onClick={() => refreshPreview({ ...printDialog, event_types: null })}
                      className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-900"
                      data-testid="print-types-all"
                    >{language === 'el' ? 'Όλα' : 'Select All'}</button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5" data-testid="print-type-grid">
                    {PRINT_TYPES.map(tp => {
                      const isActive = allTypesSelected || selectedTypes.includes(tp.key);
                      const Icon = tp.Icon;
                      return (
                        <button
                          key={tp.key} type="button"
                          onClick={() => toggleType(tp.key)}
                          aria-pressed={isActive}
                          className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs border transition-colors ${isActive ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                          data-testid={`print-type-${tp.key}`}
                        >
                          <span className={`inline-block w-3 h-3 border ${isActive ? 'bg-white border-white' : 'border-zinc-400'}`} aria-hidden="true">
                            {isActive && <span className="block w-full h-full text-zinc-900 leading-none text-[10px] font-bold text-center">✓</span>}
                          </span>
                          <Icon size={12} weight="bold" />
                          {language === 'el' ? tp.label_el : tp.label_en}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    {language === 'el'
                      ? 'Πάτα σε τύπο για να τον επιλέξεις/αποεπιλέξεις. Άδεια επιλογή = όλα.'
                      : 'Click any type to toggle it. No selection means all types.'}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Κατάσταση' : 'Status'}</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[
                      { key: 'all', label_en: 'All', label_el: 'Όλα' },
                      { key: 'pending', label_en: 'Pending', label_el: 'Εκκρεμείς' },
                      { key: 'completed', label_en: 'Completed', label_el: 'Ολοκληρωμένες' },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => refreshPreview({ ...printDialog, status: opt.key })}
                        className={`px-3 py-1.5 text-xs border transition-colors ${printDialog.status === opt.key ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`print-status-${opt.key}`}
                      >{language === 'el' ? opt.label_el : opt.label_en}</button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Προτεραιότητα' : 'Priority'}</Label>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {[
                      { key: 'all',    label_en: 'All',    label_el: 'Όλα',    dot: '' },
                      { key: 'high',   label_en: 'High',   label_el: 'Υψηλή',  dot: 'bg-red-500' },
                      { key: 'medium', label_en: 'Medium', label_el: 'Μεσαία', dot: 'bg-orange-400' },
                      { key: 'low',    label_en: 'Low',    label_el: 'Χαμηλή', dot: 'bg-emerald-500' },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => refreshPreview({ ...printDialog, priority: opt.key })}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${printDialog.priority === opt.key ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`print-priority-${opt.key}`}
                      >
                        {opt.dot && <span className={`inline-block w-2 h-2 rounded-full ${opt.dot}`}></span>}
                        {language === 'el' ? opt.label_el : opt.label_en}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date range */}
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Χρονικό Εύρος' : 'Date Range'}</Label>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {[
                      { key: 'today', label_en: 'Today', label_el: 'Σήμερα' },
                      { key: 'week',  label_en: 'This Week', label_el: 'Εβδομάδα' },
                      { key: 'month', label_en: 'This Month', label_el: 'Μήνας' },
                      { key: 'all',   label_en: 'All', label_el: 'Όλα' },
                      { key: 'custom',label_en: 'Custom…', label_el: 'Επιλογή…' },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => refreshPreview({ ...printDialog, date_range: opt.key })}
                        className={`px-3 py-1.5 text-xs border transition-colors ${printDialog.date_range === opt.key ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`print-range-${opt.key}`}
                      >{language === 'el' ? opt.label_el : opt.label_en}</button>
                    ))}
                  </div>
                  {printDialog.date_range === 'custom' && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <Label className="text-[10px]">{language === 'el' ? 'Από' : 'From'}</Label>
                        <DatePickerDMY value={printDialog.start || ''} onChange={v => refreshPreview({ ...printDialog, start: v })} />
                      </div>
                      <div>
                        <Label className="text-[10px]">{language === 'el' ? 'Έως' : 'To'}</Label>
                        <DatePickerDMY value={printDialog.end || ''} onChange={v => refreshPreview({ ...printDialog, end: v })} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Layout */}
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Διάταξη' : 'Layout'}</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[
                      { key: 'task_list', label_en: 'Daily Planner', label_el: 'Πλάνο Ημέρας' },
                      { key: 'checklist', label_en: 'Checklist',     label_el: 'Checklist' },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => setPrintDialog({ ...printDialog, layout: opt.key })}
                        className={`px-3 py-1.5 text-xs border transition-colors ${printDialog.layout === opt.key ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}
                        data-testid={`print-layout-${opt.key}`}
                      >{language === 'el' ? opt.label_el : opt.label_en}</button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { setPrintDialog(null); setPrintPreview(null); }} className="rounded-none">
              {language === 'el' ? 'Ακύρωση' : 'Cancel'}
            </Button>
            <Button
              disabled={printBusy || (printPreview?.count || 0) === 0}
              onClick={async () => {
                // Phase 811 — Open PDF inside the app's `<PdfViewerHost>`
                // modal instead of downloading. POST-fetches the PDF as a
                // blob and dispatches `emergent:open-pdf` with the blob.
                if (!printDialog) return;
                setPrintBusy(true);
                try {
                  const r = await axios.post(`${API}/calendar/tasks/export`, { ...printDialog, format: 'pdf' }, { responseType: 'blob' });
                  const raw = r.data;
                  if (!raw || raw.size < 200) {
                    toast.error(language === 'el' ? 'Το PDF είναι κενό ή έχει πρόβλημα.' : 'PDF is empty or corrupted.');
                    return;
                  }
                  const blob = raw.type === 'application/pdf' ? raw : new Blob([raw], { type: 'application/pdf' });
                  const filename = `${(printDialog.title || 'Calendar').replace(/\s+/g, '_')}.pdf`;
                  window.dispatchEvent(new CustomEvent('emergent:open-pdf', {
                    detail: { blob, filename },
                  }));
                } catch (err) {
                  // Backend error surfacing — never leave the user with a silent failure.
                  let detail = language === 'el' ? 'Η δημιουργία PDF απέτυχε.' : 'PDF generation failed.';
                  try {
                    if (err.response?.data instanceof Blob) {
                      const txt = await err.response.data.text();
                      try { const j = JSON.parse(txt); if (j.detail) detail = j.detail; } catch { /* not json */ }
                    } else if (err.response?.data?.detail) {
                      detail = err.response.data.detail;
                    }
                  } catch { /* noop */ }
                  toast.error(detail);
                } finally { setPrintBusy(false); }
              }}
              className="bg-zinc-950 rounded-none"
              data-testid="print-view-pdf-btn"
            >
              <FilePdf size={13} className="mr-1" /> {language === 'el' ? 'Προβολή PDF' : 'View PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 943 — Admin-managed calendar category manager. Same
          CategoryManager component we use in the enhanced tasks page,
          bound here to the /calendar/event-kinds collection. */}
      <CategoryManager
        open={categoryMgrOpen}
        onClose={() => setCategoryMgrOpen(false)}
        onChanged={refreshEventKinds}
        language={language}
        basePath="calendar/event-kinds"
        idField="event_kind_id"
        title={language === 'el' ? 'Διαχείριση Κατηγοριών Ημερολογίου' : 'Manage Calendar Categories'}
      />
    </Layout>
  );
}
